import { useState } from 'react'
import { inject, observer } from 'mobx-react'
import { isAlive, isStateTreeNode } from 'mobx-state-tree'
import dayjs from 'dayjs'

import { SECURITY_TYPES_TEXT, TAX_TYPES, VALUATION_METHODS } from '../../../../constants'
import {
  ActionMenu,
  DeleteModal,
  Money,
  PlanOffersOptionModal,
  ConfirmModal,
} from '../../../../components'
import { placeholderSymbolReplace, withRouterProps } from '../../../../utils'
import { API } from '../../../../api'
import { typeToAPI } from '../../utils'
import { Row, Type, Ticker, Name, NameLink, Balance, RowMenu } from './InvestmentRowStyle'

/**
 * Renders an investment row and manages its edit and removal flow.
 */
const InvestmentGroupItem = props => {
  // Removal flow: closed → confirm, or closed → planOffer → confirmPlanOffer/confirmNonPlanOffer.
  const [deleteStep, setDeleteStep] = useState('closed')
  const [status, setStatus] = useState('done')

  const handleModalClose = () => {
    setDeleteStep('closed')
    setStatus('done')
  }

  const toggleDeleteModal = e => {
    e?.stopPropagation()

    const {
      store,
      match: {
        params: { id: accountId },
      },
      investment,
    } = props

    const { employerAccounts = [] } = store
    const isEmployerAccount = employerAccounts.some(account => account.id === Number(accountId))

    if (isEmployerAccount) {
      const { investments } = props.account
      const lastSecurityLeft =
        investments.filter(
          inv =>
            inv.securities?.id === investment.securities?.id &&
            inv.taxType !== TAX_TYPES.notInvested
        ).length === 1

      if (lastSecurityLeft) {
        setDeleteStep('planOffer')
        return
      }
    }

    setDeleteStep('confirm')
  }

  const handlePlanOffer = stillOffered => {
    setDeleteStep(stillOffered ? 'confirmPlanOffer' : 'confirmNonPlanOffer')
  }

  const handleConfirmDelete = async () => {
    setStatus('loading')

    try {
      const {
        features,
        store,
        store: { institutionalAccount },
        match: {
          params: { type, id },
        },
        account: { getAvailableInvestments },
        investment,
      } = props
      const isInstitutionalAccount = props.account?.account?.id === institutionalAccount?.id

      if (deleteStep === 'confirmPlanOffer') {
        await API.put(`${typeToAPI[type]}/${id}/investments`, {
          ...investment,
          dateOfUserInfo: dayjs().format('YYYY-MM-DD'),
          quantity: 0,
          totalCostBasis: 0,
          totalValue: 0,
          valuationMethod: VALUATION_METHODS.notInvested,
          taxType: TAX_TYPES.notInvested,
        })
      } else {
        await API.delete(`${typeToAPI[type]}/${id}/investments/${investment.id}`)
      }

      await store.getAccounts()
      if (isInstitutionalAccount && features.editInstitutionalAccount) {
        await getAvailableInvestments()
      }

      await props.account.getInvestments({ type, id })

      handleModalClose()
    } catch (err) {
      console.error(err)
      setStatus('error')
    }
  }

  const { canEdit, toggleEdit } = props

  // Reloading an account's investments replaces the whole list, destroying the nodes the mounted
  // rows were handed. This row observes its node, so its own reaction can re-run against the
  // destroyed one before the parent re-renders the list without it. Nothing to draw in that gap.
  // isAlive throws on anything that is not a node, so only nodes are checked.
  if (isStateTreeNode(props.investment) && !isAlive(props.investment)) return null

  const { taxType, value, securities: security, id } = props.investment
  const { securityType, securityName, ticker } = security
  const securityTypeName = SECURITY_TYPES_TEXT[securityType]
  const displayName = placeholderSymbolReplace(securityName)

  // The not-invested tax type marks a plan option the participant holds no balance in.
  const isInvested = taxType !== TAX_TYPES.notInvested
  const isEditable = isInvested && canEdit && typeof toggleEdit === 'function'

  const handleEdit = event => toggleEdit(id, event)

  return (
    <Row>
      <Type>{securityTypeName}</Type>
      <Ticker>{ticker}</Ticker>
      <Name>
        {isEditable ? (
          <NameLink type='button' onClick={handleEdit}>
            {displayName}
          </NameLink>
        ) : (
          displayName
        )}
      </Name>
      {isInvested && (
        <Balance>
          <Money value={value} />
        </Balance>
      )}
      {isEditable && (
        <RowMenu>
          <ActionMenu
            items={[
              { label: 'Edit', onClick: handleEdit },
              { label: 'Remove', onClick: toggleDeleteModal, danger: true },
            ]}
            ariaLabel={`Actions for ${displayName}`}
          />
        </RowMenu>
      )}
      {/* Mount the modals only during removal to avoid registering three keydown listeners per row. */}
      {deleteStep !== 'closed' && (
        <>
          <DeleteModal
            handleConfirmation={handleConfirmDelete}
            isModalOpen={deleteStep === 'confirm'}
            title={'Delete investment: ' + displayName + '?'}
            toggleModal={handleModalClose}
            loading={status === 'loading'}
            error={status === 'error'}
          />
          <PlanOffersOptionModal
            isModalOpen={deleteStep === 'planOffer'}
            handleCancel={handleModalClose}
            handleConfirmation={() => handlePlanOffer(true)}
            handleNotOffered={() => handlePlanOffer(false)}
            title='Does your plan still offer this as an option for you to invest in?'
          />
          <ConfirmModal
            handleConfirm={handleConfirmDelete}
            isModalOpen={['confirmPlanOffer', 'confirmNonPlanOffer'].includes(deleteStep)}
            toggleModal={handleModalClose}
            title='Got it. Thanks.'
            secondaryText='Your investment will be removed. This action cannot be undone.'
            confirmButtonText='Save'
            denyButtonText='Cancel'
            loading={status === 'loading'}
            error={status === 'error'}
          />
        </>
      )}
    </Row>
  )
}

export default withRouterProps(inject('store', 'account')(observer(InvestmentGroupItem)))
